import styled from 'styled-components';
import React, { useState, useEffect } from 'react';
import { modularOutcomeTypeQuestion } from 'constants/questionTypes';
import { words } from '../translations';
import SkipCheckbox from './SkipCheckbox';

// Dynamic function to generate Likert choices based on scale size
const generateLikertChoices = (scaleSize) => {
  const numericScale = Number(scaleSize);
  
  if (numericScale === 5) {
    return [
      { value: 2, label: 'Strongly Disagree', emoji: '1' },
      { value: 4, label: 'Disagree', emoji: '2' },
      { value: 6, label: 'Neutral', emoji: '3' },
      { value: 8, label: 'Agree', emoji: '4' },
      { value: 10, label: 'Strongly Agree', emoji: '5' },
    ];
  } else if (numericScale === 7) {
    return [
      { value: 1.43, label: 'Strongly Disagree', emoji: '1' },
      { value: 2.86, label: 'Disagree', emoji: '2' },
      { value: 4.29, label: 'Slightly Disagree', emoji: '3' },
      { value: 5.71, label: 'Neutral', emoji: '4' },
      { value: 7.14, label: 'Slightly Agree', emoji: '5' },
      { value: 8.57, label: 'Agree', emoji: '6' },
      { value: 10, label: 'Strongly Agree', emoji: '7' },
    ];
  } else {
    // Fallback to original 6-point scale if not 5 or 7
    return [
      { value: 1.66, label: 'Strongly Disagree', emoji: '1' },
      { value: 3.333, label: 'Disagree', emoji: '2' },
      { value: 5, label: 'Slightly Disagree', emoji: '3' },
      { value: 6.666, label: 'Slightly Agree', emoji: '4' },
      { value: 8.333, label: 'Agree', emoji: '5' },
      { value: 10, label: 'Strongly Agree', emoji: '6' },
    ];
  }
};

const LikertScale = ({
  id,
  resetResponsesOnChange,
  question,
  saveUpdatedAnswers,
  currentQuestionDet,
  questionSequenceMap,
  language,
  useLikert
}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [isNA, setNA] = useState(false);

  // Generate choices based on useLikert value
  const likertChoices = generateLikertChoices(useLikert);

  useEffect(() => {
    // If there's a saved response, set it.
    if (currentQuestionDet?.response) {
      if (currentQuestionDet.response.response === 'N/A') {
        setNA(true);
        setSelectedValue(null);
      } else {
        setSelectedValue(currentQuestionDet.response.response);
        setNA(false);
      }
    } else {
      // Or default to null or a specific value
      setSelectedValue(null);
      setNA(false);
    }
  }, [currentQuestionDet]);

  const handleChoiceClick = (choiceValue) => {
    setSelectedValue(choiceValue);
    setNA(false);
    saveValue(choiceValue);
  };

  const handleNAClick = () => {
    if (!isNA) {
      // Selecting N/A
      setNA(true);
      setSelectedValue(null);
      SaveResponse('N/A');
    } else {
      // Deselecting N/A - We need to properly reset the state
      setNA(false);
      setSelectedValue(null);
      
      // Create a response object that indicates no selection was made

      const QuesResp = {
        order: currentQuestionDet?.id,
        clicked: false,
        s: currentQuestionDet?.s,
        q: currentQuestionDet?.q,
        id: currentQuestionDet?.id,
        ques_order: currentQuestionDet?.id,
        scale: question?.scale,
        question: question?.question,
        response: null,
        ques_sort_order: currentQuestionDet?.id,
      };
      saveUpdatedAnswers(
        currentQuestionDet.id,
        QuesResp,
        modularOutcomeTypeQuestion
      );
      
     
    }
  };

  const saveValue = (value) => {
    SaveResponse(value);
  };

   // save the response
   function SaveResponse(newValue) {
    // reset associated questions
    // check if value changed
    if (currentQuestionDet?.response &&
      currentQuestionDet?.response?.response !== newValue) {
      if (currentQuestionDet?.resetOnChange) {
        // resetResponsesOnChange
        if (resetResponsesOnChange[currentQuestionDet?.id]) {
          // reset the responses to each of the linked questions
          // console.log(resetResponsesOnChange[currentQuestionDet?.id]);
          resetResponsesOnChange[currentQuestionDet?.id].map((sq) => {
            console.log("questionSequenceMap[sq].response ", questionSequenceMap[sq]);
            questionSequenceMap[sq].response = false;
            questionSequenceMap[sq].clicked = false;
            return null;
          });
        }
      }
    }

    const QuesResp = {
      order: currentQuestionDet?.id,
      clicked: true,
      s: currentQuestionDet?.s,
      q: currentQuestionDet?.q,
      id: currentQuestionDet?.id,
      ques_order: currentQuestionDet?.id,
      scale: question?.scale,
      question: question?.question,
      response: newValue,
      ques_sort_order: currentQuestionDet?.id,
    };
    saveUpdatedAnswers(
      currentQuestionDet.id,
      QuesResp,
      modularOutcomeTypeQuestion
    );
  }

 

  return (
    <Container>

      <ChoicesWrapper>
        {likertChoices.map((choice) => {
          const isSelected = choice.value === selectedValue;
          return (
            <ChoiceButton
              key={choice.value}
              isSelected={isSelected}
              onClick={() => handleChoiceClick(choice.value)}
            >
              <EmojiSpan>{choice.emoji}</EmojiSpan>
              <LabelSpan>{words[choice.label][language]}</LabelSpan>
            </ChoiceButton>
          );
        })}
      </ChoicesWrapper>

      {question?.allowSkip && (
        <SkipCheckbox
          id={`skip_${currentQuestionDet?.id}`}
          selected={isNA}
          onClick={handleNAClick}
          language={language}
        />
      )}
    </Container>
  );
};

export default LikertScale;

/* -- STYLED COMPONENTS -- */

const Container = styled.div`
  width: 100%;
  padding: 20px 0;
  user-select: none;
`;

const QuestionText = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: left;
`;

const ChoicesWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 500px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ChoiceButton = styled.button`
  flex: 1;
  margin: 0 5px;
  background-color: ${(props) => (props.isSelected ? '#D5E2F9' : '#fff')};
  border: 2px solid
    ${(props) => (props.isSelected ? '#2D70E2' : 'transparent')};
  border-radius: 8px;
  padding: 12px 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  height: 100px;
  position: relative;

  &:hover {
    background-color: #D5E2F9;
  }

  @media (max-width: 500px) {
    height: 60px;
    width: 100%;
    margin: 0;
    display: flex;
    align-items: center;
    padding: 8px 16px;
  }
`;

const EmojiSpan = styled.span`
  font-size: 24px;
  text-align: center;
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;

  @media (max-width: 500px) {
    position: static;
    margin-right: 16px;
  }
`;

const LabelSpan = styled.span`
  font-size: 12px;
  text-align: center;
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;

  @media (max-width: 500px) {
    position: static;
    font-size: 14px;
    text-align: left;
  }
`;

const ScaleLabelsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const ScaleLabel = styled.span`
  font-size: 14px;

  @media (max-width: 500px) {
    font-size: ${(props) => (props.language === 'EN' ? '14px' : '12px')};
  }
`;


const NA = styled.div`
  font-size: 12px;
  font-weight: bold;
  font-family: "Barlow", sans-serif;
  background-color: ${props => props.selected ? '#D5E2F9' : '#fff'};
  color: #333;
  height: 30px;
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  display: flex;
  width: 75px;
  margin-top: 50px;
  cursor: pointer;
  margin-right: -20px;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.selected ? '#2D70E2' : 'transparent'};
  
  &:hover {
    background-color: #D5E2F9;
  }

  @media(max-width:768px){
    margin-right: -10px;
  }
`

